import { Card, Row, Col, Statistic, Progress } from "antd";
import {
  Bar,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
  Legend,
  Area,
  AreaChart,
  Cell,
} from "recharts";
import { ArrowUpOutlined, AlertOutlined, SafetyOutlined, BankOutlined } from "@ant-design/icons";
import { IntelligenceDto } from "dtos/application.dto";
import DebtToIncomeChart from "./charts/DebtToIncomeChart";
import { AppCard } from "components/AppCard";
import { getValueColor } from "utils/functions";

type OverViewProps = {
  data: IntelligenceDto;
};

const CashFlowPage = ({ data }: OverViewProps) => {
  return (
    <div>
      <div className="p-6 bg-white min-h-screen">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-800 m-0">Cash Flow Analysis</h1>
          <p className="text-gray-500 mt-2">Comprehensive cash flow, liquidity, and financial stability analysis</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <AppCard className="flex flex-col gap-2 min-w-0">
            <div className="flex items-center gap-2">
              <SafetyOutlined
                className="text-base"
                style={{ color: getValueColor(data.cash_flow_stability_index ?? 0) }}
              />
              <span className="text-xs text-gray-500">Cash Flow Stability</span>
            </div>
            <div className="font-bold text-2xl" style={{ color: getValueColor(data.cash_flow_stability_index ?? 0) }}>
              {data.cash_flow_stability_index}
              <span className="text-sm font-normal text-gray-400 ml-1">/10</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-1.5">
              <div
                className="h-1.5 rounded-full transition-all"
                style={{
                  width: `${Math.max(0, Math.min(100, (data.cash_flow_stability_index ?? 0) * 10))}%`,
                  backgroundColor: getValueColor(data.cash_flow_stability_index ?? 0),
                }}
              />
            </div>
            <span className="text-xs text-gray-400 truncate" title={data.cash_flow_volatility_risk}>
              {data.cash_flow_volatility_risk}
            </span>
          </AppCard>

          <AppCard className="flex flex-col gap-2 min-w-0">
            <div className="flex items-center gap-2">
              <BankOutlined className="text-base text-blue-500" />
              <span className="text-xs text-gray-500">Average Daily Balance</span>
            </div>
            <div
              className="font-bold text-2xl truncate"
              style={{ color: getValueColor(data.average_daily_closing_balance ?? 0) }}
            >
              ${Number(data.average_daily_closing_balance).toLocaleString(undefined, { maximumFractionDigits: 2 })}
            </div>
            <div className="flex items-center gap-1">
              <span className="text-xs text-gray-400">Min:</span>
              <span className="text-xs text-red-400 truncate">
                ${Number(data.min_daily_balance).toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </span>
            </div>
          </AppCard>

          <AppCard className="flex flex-col gap-2 min-w-0">
            <div className="flex items-center gap-2">
              <AlertOutlined className="text-base text-yellow-500" />
              <span className="text-xs text-gray-500">Liquidity Risk Index</span>
            </div>
            <div className="font-bold text-2xl text-yellow-500">
              {data.liquidity_risk_index}
              <span className="text-sm font-normal text-gray-400 ml-1">/100</span>
            </div>
            <span className="text-xs text-red-500 font-medium truncate" title={data.liquidity_risk}>
              {data.liquidity_risk}
            </span>
            <span className="text-xs text-gray-400 truncate" title={data.balance_volatility_risk}>
              {data.balance_volatility_risk}
            </span>
          </AppCard>

          <AppCard className="flex flex-col gap-2 min-w-0">
            <div className="flex items-center gap-2">
              <ArrowUpOutlined className="text-base text-purple-500" />
              <span className="text-xs text-gray-500">Savings Rate</span>
            </div>
            <div className="font-bold text-2xl" style={{ color: getValueColor(data.savings_rate_percentage ?? 0) }}>
              {data.savings_rate_percentage}
              <span className="text-sm font-normal text-gray-400 ml-1">%</span>
            </div>
            <span className="text-xs text-gray-400 truncate" title={data.relative_volatility_risk}>
              {data.relative_volatility_risk}
            </span>
          </AppCard>
        </div>

        <Row gutter={16} style={{ marginBottom: "24px" }}>
          <Col span={12}>
            <Card title="Monthly Revenue Distribution" className=" border border-gray-300">
              <div style={{ height: "300px" }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={data.revenue_distribution_stats}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="month" tick={{ fill: "#666", fontSize: 12 }} />
                    <YAxis tick={{ fill: "#666", fontSize: 12 }} />
                    <Tooltip formatter={(value) => [`$${Number(value).toLocaleString()}`, ""]} />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="max"
                      stackId="1"
                      stroke="#1890ff"
                      fill="#1890ff"
                      fillOpacity={0.3}
                      name="Max"
                    />
                    <Area
                      type="monotone"
                      dataKey="mean"
                      stackId="2"
                      stroke="#52c41a"
                      fill="#52c41a"
                      fillOpacity={0.5}
                      name="Mean"
                    />
                    <Area
                      type="monotone"
                      dataKey="min"
                      stackId="3"
                      stroke="#faad14"
                      fill="#faad14"
                      fillOpacity={0.7}
                      name="Min"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </Col>

          <Col span={12}>
            <DebtToIncomeChart data={data.debt_to_income_ratio} />
          </Col>
        </Row>

        <div className="grid grid-cols-1 sm:grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
          <AppCard className="flex flex-col justify-between gap-3 border border-gray-300">
            <h3 className="text-base font-semibold">Payroll to Revenue</h3>

            <div className="flex flex-col items-center text-center">
              <Progress
                type="circle"
                percent={data?.payroll_to_revenue?.payroll_to_revenue_percent}
                format={(percent) => `${percent}%`}
                strokeColor="#52c41a"
              />

              <div className="mt-4 w-full">
                <Statistic
                  title="Total Payroll"
                  value={data?.payroll_to_revenue?.total_payroll}
                  formatter={(value) => `${Number(value).toLocaleString()}`}
                />
              </div>
            </div>
          </AppCard>

          <AppCard className="flex flex-col justify-between gap-3 border border-gray-300">
            <h3 className="text-base font-semibold">Expense to Revenue</h3>

            <div className="flex flex-col items-center text-center">
              <Progress
                type="circle"
                percent={data?.expense_to_revenue?.expense_to_revenue_percent}
                format={(percent) => `${percent}%`}
                strokeColor="#faad14"
              />

              <div className="mt-4 w-full">
                <Statistic
                  title="Total Expenses"
                  value={data?.expense_to_revenue?.total_expenses}
                  formatter={(value) => `${Number(value).toLocaleString()}`}
                />
              </div>
            </div>
          </AppCard>

          <AppCard className="flex flex-col gap-3 border border-gray-300 min-w-0">
            <h3 className="text-base font-semibold">Deposit Summary</h3>

            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col">
                <span className="text-xs text-gray-400">Total Deposits</span>
                <span className="text-base font-semibold truncate">{data?.deposit_summary?.number_of_deposits}</span>
              </div>

              <div className="flex flex-col">
                <span className="text-xs text-gray-400">Total Amount</span>
                <span className="text-base font-semibold truncate">
                  {data?.deposit_summary?.total_deposit_amount?.toLocaleString()}
                </span>
              </div>

              <div className="flex flex-col">
                <span className="text-xs text-gray-400">Average Deposit</span>
                <span className="text-base font-semibold truncate">
                  {data?.deposit_summary?.average_deposit?.toLocaleString()}
                </span>
              </div>

              <div className="flex flex-col">
                <span className="text-xs text-gray-400">Std Deviation</span>
                <span className="text-base font-semibold truncate">
                  {data?.deposit_summary?.std_dev?.toLocaleString()}
                </span>
              </div>

              <div className="col-span-2 flex flex-col">
                <span className="text-xs text-gray-400">Range (Min → Max)</span>
                <span className="text-base font-semibold truncate">
                  {data?.deposit_summary?.min?.toLocaleString()} → {data?.deposit_summary?.max?.toLocaleString()}
                </span>
              </div>

              <div className="col-span-2 border-t border-gray-100 pt-2">
                <span className="text-xs text-gray-400 block mb-1.5">Percentiles</span>

                <div className="grid grid-cols-3 gap-2">
                  {Object.entries(data?.deposit_summary?.percentiles ?? {}).map(([key, value]) => (
                    <div key={key} className="flex flex-col bg-gray-50 rounded-lg p-2 min-w-0">
                      <span className="text-xs text-gray-400">{key}</span>
                      <span className="text-sm font-semibold text-gray-700 truncate">
                        {Number(value).toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </AppCard>
        </div>

        <Card title="Category Recurrence Analysis" className="border border-gray-300">
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.category_recurrence_analysis} margin={{ top: 10, right: 20, left: 0, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                <XAxis
                  dataKey="category"
                  tick={{ fill: "#6b7280", fontSize: 11 }}
                  angle={-35}
                  textAnchor="end"
                  tickLine={false}
                  axisLine={{ stroke: "#e5e7eb" }}
                  interval={0}
                />
                <YAxis
                  tick={{ fill: "#6b7280", fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(v) => `$${Number(v).toLocaleString()}`}
                />
                <Tooltip
                  contentStyle={{ backgroundColor: "white", border: "1px solid #e5e7eb", borderRadius: "8px" }}
                  formatter={(value: number, _: string, props: any) => {
                    const p = props.payload;

                    return (
                      <div className="text-xs space-y-1">
                        <div>
                          <span className="text-gray-400">Category:</span> <strong>{p.category}</strong>
                        </div>
                        <div>
                          <span className="text-gray-400">Avg:</span>{" "}
                          <strong>${p.average_amount?.toLocaleString()}</strong>
                        </div>
                        <div>
                          <span className="text-gray-400">Median:</span> <strong>{p.median?.toLocaleString()}</strong>
                        </div>
                        <div>
                          <span className="text-gray-400">Mode:</span> <strong>{p.mode?.toLocaleString()}</strong>
                        </div>
                        <div>
                          <span className="text-gray-400">Range:</span>{" "}
                          <strong>
                            ${p.min_amount?.toLocaleString()} – ${p.max_amount?.toLocaleString()}
                          </strong>
                        </div>
                        <div>
                          <span className="text-gray-400">Pattern:</span> <strong>{p.best_recurrence_fit}</strong>
                        </div>
                        <div>
                          <span className="text-gray-400">Transactions:</span> <strong>{p.total_transactions}</strong>
                        </div>
                      </div>
                    );
                  }}
                />
                <Bar dataKey="average_amount" radius={[4, 4, 0, 0]}>
                  {data?.category_recurrence_analysis?.map((entry: any, index: number) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={["#3b82f6", "#8b5cf6", "#10b981", "#f59e0b", "#ef4444", "#06b6d4", "#ec4899"][index % 7]}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="mt-4 border-t border-gray-100 pt-4 overflow-x-auto">
            <table className="w-full text-xs text-gray-600">
              <thead>
                <tr className="text-gray-400 border-b border-gray-100">
                  <th className="text-left pb-2 font-medium">Category</th>
                  <th className="text-right pb-2 font-medium">Txns</th>
                  <th className="text-right pb-2 font-medium">Avg</th>
                  <th className="text-right pb-2 font-medium">Median</th>
                  <th className="text-right pb-2 font-medium">Max</th>
                  <th className="text-right pb-2 font-medium">Pattern</th>
                </tr>
              </thead>
              <tbody>
                {data?.category_recurrence_analysis?.map((item: any, index: number) => (
                  <tr key={item.category} className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="py-1.5 flex items-center gap-1.5">
                      <span
                        className="w-2 h-2 rounded-full inline-block"
                        style={{
                          backgroundColor: [
                            "#3b82f6",
                            "#8b5cf6",
                            "#10b981",
                            "#f59e0b",
                            "#ef4444",
                            "#06b6d4",
                            "#ec4899",
                          ][index % 7],
                        }}
                      />
                      <span className="capitalize">{item.category}</span>
                    </td>
                    <td className="py-1.5 text-right">{item.total_transactions}</td>
                    <td className="py-1.5 text-right">${item.average_amount?.toLocaleString()}</td>
                    <td className="py-1.5 text-right">${item.median?.toLocaleString()}</td>
                    <td className="py-1.5 text-right">${item.max_amount?.toLocaleString()}</td>
                    <td className="py-1.5 text-right">
                      <span className="bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded text-xs capitalize">
                        {item.best_recurrence_fit}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default CashFlowPage;
